<script setup lang="ts">
import { RouterView } from 'vue-router'
import { computed, type ComputedRef, onMounted, onUnmounted, ref, watch } from 'vue'

import logo from '@/assets/FHBC-logo.svg'

/** Title */
const title = import.meta.env.VITE_APP_TITLE ?? 'Vuetify3 Application'

import { useGlobalStore } from '@/stores/global'
import { useConfigStore } from '@/stores/config'
import { useTheme } from 'vuetify'
import { routerPush } from '@/router'

import { storeToRefs } from 'pinia'
import type { CurrentPathInfo } from '@/stores/config'
import FooterContent from '@/components/FooterContent.vue'

import { useRoute } from 'vue-router'
import MainNavComponent from '@/components/navigation/MainNavComponent.vue'
import LoginForm from '@/components/user/LoginForm.vue'
const route = useRoute()

/** Vuetify Theme */
const theme = useTheme()

/** Config Store */
const configStore = useConfigStore()

/** Global Store */
const globalStore = useGlobalStore()
const currentGlobalStore = storeToRefs(globalStore)
const sbItems = ref(currentGlobalStore.sbQueueMessages)

const currentRoute = ref<CurrentPathInfo>()
watch(route, () => {
  currentRoute.value = {
    name: route.name,
    path: route.path,
    params: route.params
  }
})

/** loading overlay visibility */
// const loading: WritableComputedRef<boolean> = computed({
//   get: () => globalStore.loadingCounter > 0,
//   set: (v) => globalStore.setLoading(v)
// })

const loading = computed({
  get: () => globalStore.loadingCounter > 0,
  set: (v) => globalStore.setLoading(v)
})

const showLoginFormDialog = ref(false)
function checkUrlHash() {
  // Open modal if the hash matches a specific value
  showLoginFormDialog.value = window.location.hash === '#login'
}
function closeLoginFormDialog() {
  showLoginFormDialog.value = false
  window.location.hash = ''
  // window.history.pushState(null, '', ' '); // Removes the hash
}

/** Toggle Dark mode */
const isDark: ComputedRef<string> = computed(() => (configStore.theme ? 'dark' : 'light'))
onMounted(() => {
  document.title = title

  configStore.getAppMaintenanceStatus(currentRoute.value)
  configStore.loadBaseData()
  configStore.loadAppData()

  window.addEventListener('hashchange', checkUrlHash)

  if (window.location.hash === '#login') {
    showLoginFormDialog.value = true
  }

  setInterval(
    async () => {
      // check current app status
      configStore.getAppMaintenanceStatus(currentRoute.value)
    },
    1000 * 60 * 5
  )
})

// cleanup on unmount
onUnmounted(() => {
  window.removeEventListener('hashchange', checkUrlHash)
})
</script>
<template>
  <v-layout :theme="isDark" class="rounded rounded-md">
    <v-app-bar density="prominent">
      <v-container>
        <v-row>
          <div class="d-flex w-100 justify-space-between align-center">
            <img
              class="cursor-pointer m-4 logo"
              @click="routerPush('home')"
              :src="logo"
              alt="logo"
            />

            <div class="d-flex align-center justify-end py-4">
              <MainNavComponent />
            </div>
          </div>
        </v-row>
      </v-container>
    </v-app-bar>

    <v-main class="d-flex align-center justify-center mx-auto" style="min-height: 300px">
      <router-view v-slot="{ Component, route }">
        <component :is="Component" :key="route.name" />
      </router-view>
    </v-main>

    <v-snackbar-queue v-model:modelValue="sbItems">
      <template v-slot:actions>
        <v-btn icon="mdi-close-circle"></v-btn>
      </template>
    </v-snackbar-queue>

    <v-footer app elevation="3">
      <FooterContent />
    </v-footer>
  </v-layout>
  <teleport to="head">
    <meta name="theme-color" :content="theme.computedThemes.value['light'].colors.background" />
    <link rel="icon" :href="logo" type="image/svg+xml" />
  </teleport>

  <v-overlay v-model="loading" class="justify-center align-center">
    <v-progress-circular indeterminate size="64" />
  </v-overlay>

  <!-- login modal -->
  <v-dialog
    persistent
    v-model="showLoginFormDialog"
    class="dialog-modal-small"
    scrollable
    @keydown.esc="closeLoginFormDialog"
  >
    <v-card class="ma-0 pa-0" :loading="loading">
      <v-card-title class="text-white bg-lightblue d-flex justify-space-between align-center">
        <span class="card-title-content">Account Login</span>
        <v-spacer />
        <v-icon
          class="text-white"
          icon="mdi-close"
          size="small"
          @click="closeLoginFormDialog"
        ></v-icon>
      </v-card-title>
      <v-card-text>
        <LoginForm v-bind:isModal="true" @close="closeLoginFormDialog" />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style lang="scss">
// @use 'vuetify/_settings';
@use '@/styles/style' as *;

.v-btn-group {
  background-color: transparent;
  .v-btn {
    color: white;
    background-color: transparent;
  }
  .v-badge {
    color: white;
  }

  v-icon {
    background-color: $fhbc-green;
    color: white;
  }
  .v-badge__badge.v-theme--light {
    background-color: $fhbc-red;
    color: white;
  }
}

.v-toolbar__content {
  background-color: #ffffff;
  color: $fhbc-darkblue;
  .logo {
    width: 120px;
    max-height: 115px;

    //align-items: unset !important;
  }
}
</style>
